import { Doughnut } from "react-chartjs-2";
import "./chartSetup";
import { useMemo } from "react";

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

interface MDTDonutProps {
  segments: DonutSegment[];
  total: number;
  size?: number;
  thickness?: number;
  centerLabel?: string;
}

const DONUT_BACKGROUND = "#E4EAF0";
const CENTER_VALUE_COLOR = "#16181D";
const CENTER_LABEL_COLOR = "#8A929B";

const donutPlugin = {
  id: "lymphoDonutCenter",
  beforeDatasetsDraw: (chart: any) => {
    const arc = chart.getDatasetMeta(0)?.data?.[0];
    if (!arc) return;
    const { x, y, innerRadius, outerRadius } = arc;
    const ctx = chart.ctx;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
    ctx.arc(x, y, innerRadius, 0, Math.PI * 2, true);
    ctx.fillStyle = DONUT_BACKGROUND;
    ctx.fill();
    ctx.restore();
  },
  afterDatasetsDraw: (chart: any) => {
    const opts = chart.config?.options?.plugins?.lymphoDonutCenter;
    if (!opts) return;
    const arc = chart.getDatasetMeta(0)?.data?.[0];
    if (!arc) return;
    const { x, y } = arc;
    const { total, label, size } = opts;
    const ctx = chart.ctx;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = CENTER_VALUE_COLOR;
    ctx.font = `700 ${Math.round(size * 0.16)}px Arial, sans-serif`;
    ctx.fillText(String(total), x, y - Math.round(size * 0.03));
    ctx.fillStyle = CENTER_LABEL_COLOR;
    ctx.font = `400 ${Math.round(size * 0.08)}px Arial, sans-serif`;
    ctx.fillText(label, x, y + Math.round(size * 0.1));
    ctx.restore();
  },
};

const MDTDonut = ({ segments, total, size = 190, thickness = 28, centerLabel = "Total MDTs" }: MDTDonutProps) => {
  const hasData = total > 0;
  const radius = (size - thickness) / 2;
  const cx = size / 2;

  const chartData = useMemo(
    () => ({
      labels: segments.map((s) => s.label),
      datasets: [
        {
          data: segments.map((s) => s.value),
          backgroundColor: segments.map((s) => s.color),
          borderColor: "transparent",
          borderWidth: 0,
          borderRadius: 0,
          spacing: 0,
        },
      ],
    }),
    [segments]
  );

  const options: any = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1,
    cutout: `${Math.round((1 - (2 * thickness) / size) * 100)}%`,
    plugins: {
      legend: { display: false },
      tooltip: { displayColors: false },
      lymphoDonutCenter: { total, label: centerLabel, size },
    },
    layout: { padding: 0 },
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        {hasData ? (
          <Doughnut data={chartData} options={options} plugins={[donutPlugin]} />
        ) : (
          <div className="flex items-center justify-center" style={{ width: size, height: size }}>
            <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
              <circle cx={cx} cy={cx} r={radius} fill="none" stroke={DONUT_BACKGROUND} strokeWidth={thickness} />
              <text x={cx} y={cx - 4} textAnchor="middle" dominantBaseline="middle" className="fill-[#16181D] font-bold">
                0
              </text>
              <text x={cx} y={cx + Math.round(size * 0.11)} textAnchor="middle" dominantBaseline="middle" className="fill-[#8A929B]">
                {centerLabel}
              </text>
            </svg>
          </div>
        )}
      </div>

      <ul className="space-y-3 min-w-[140px]">
        {segments.map((seg) => {
          const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
          return (
            <li key={seg.label} className="flex items-center gap-2.5">
              <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: seg.color }} aria-hidden="true" />
              <span className="text-[13px] font-medium text-[#16181D] flex-1">{seg.label}</span>
              <span className="text-[13px] font-semibold text-[#16181D]">{seg.value}</span>
              <span className="w-10 text-right text-[12px] text-[#8A929B]">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MDTDonut;